import type {FC, ReactNode} from 'react';
import clsx from 'clsx';
import Image from 'next/image';

import {Container} from '@/modules/foundation/components/container';
import {EnvelopeIcon} from '@/modules/foundation/components/icons/envelope-icon';
import {MapIcon} from '@/modules/foundation/components/icons/map-icon';
import {PhoneIcon} from '@/modules/foundation/components/icons/phone-icon';
import {Link} from '@/modules/foundation/components/link';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {createPhoneLink, formatPhoneNumber} from '@/modules/foundation/shared/format';
import type {KeyedArray} from '@/modules/foundation/shared/types';

type Address = {
	streetName?: string;
	postalCode?: string;
	city?: string;
};

type Organization = {
	address?: Address;
	phoneNumber?: string;
	email?: string;
};

type FooterLink = {
	href?: string;
	title?: string;
	targetBlank?: boolean;
};

export type FooterProps = {
	socialMediaLinks?: KeyedArray<FooterLink>;
	organization?: Organization;
	links?: KeyedArray<FooterLink>;
};

export const Footer: FC<FooterProps> = ({links, organization, socialMediaLinks}) => {
	const {address, phoneNumber, email} = organization || {};

	return (
		<Container
			as="footer"
			className={clsx(
				'bg-primary',
				'text-white',
				'before:absolute',
				'before:inset-0',
				'before:-z-50',
				'before:shadow-[0_0_900px_200px_rgba(229,248,255,0.9)]',
			)}
			spacingTop="normal"
			spacingBottom="normal"
			hasBackground
		>
			<Image src="/logo-white.svg" alt="" width={270} height={50} className={clsx('mb-12')} />
			<div className={clsx('grid', 'grid-cols-1', 'lg:grid-cols-2', 'gap-6')}>
				<div
					className={clsx(
						'flex',
						'flex-col',
						'gap-4',
						'max-md:mb-2',
						'max-md:pb-6',
						'max-md:border-b',
						'border-[#4D80A5]',
					)}
				>
					{address && (
						<div className={clsx('flex', 'items-center', 'gap-4')}>
							<MapIcon aria-label="adresse" className={clsx('shrink-0')} />
							<Paragraph size="small" className={clsx('relative', '-mt-2')}>
								<span>{address.streetName}</span>, <span>{address.postalCode}</span>{' '}
								<span>{address.city}</span>
							</Paragraph>
						</div>
					)}

					{phoneNumber && (
						<div className={clsx('flex', 'items-center', 'gap-4')}>
							<PhoneIcon aria-label="telefon" className={clsx('shrink-0')} />
							<Link
								href={createPhoneLink(phoneNumber)}
								className={clsx(
									'no-underline',
									'hover:underline',
									'relative',
									'-mt-2',
								)}
							>
								{formatPhoneNumber(phoneNumber)}
							</Link>
						</div>
					)}

					{email && (
						<div className={clsx('flex', 'items-center', 'gap-4')}>
							<EnvelopeIcon aria-label="epost" className={clsx('shrink-0')} />
							<Link
								href={`mailto:${email.replaceAll(' ', '')}`}
								className={clsx(
									'no-underline',
									'hover:underline',
									'relative',
									'-mt-2',
								)}
							>
								{email}
							</Link>
						</div>
					)}
				</div>

				<div className={clsx('grid', 'grid-cols-1', 'sm:grid-cols-2', 'gap-6')}>
					{links?.length && (
						<div>
							<FooterTitle>Innhold</FooterTitle>
							<ul className={clsx('grid', 'gap-2')}>
								{links.map((link) => {
									return (
										<li key={link._key}>
											<Link
												href={link.href}
												targetBlank={link.targetBlank}
												className={clsx('no-underline', 'hover:underline')}
											>
												{link.title}
											</Link>
										</li>
									);
								})}
							</ul>
						</div>
					)}

					{socialMediaLinks?.length && (
						<div>
							<FooterTitle>Sosiale medier</FooterTitle>
							<ul className={clsx('grid', 'gap-2')}>
								{socialMediaLinks.map((link) => (
									<li key={link._key}>
										<Link
											href={link.href}
											targetBlank={link.targetBlank}
											className={clsx('no-underline', 'hover:underline')}
										>
											{link.title}
										</Link>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			</div>
		</Container>
	);
};

const FooterTitle = ({children}: {children: ReactNode}) => (
	<Heading level={2} size="large" spacing className="font-semibold text-inherit">
		{children}
	</Heading>
);
